import { KeyRound, LogOut, Settings, ShieldCheck, UserCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/use-auth';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { resolveDiscordUrl } from '@/lib/discord-url';
import { ADMIN_PANEL_URL, canAccessAdminPanel } from '@/lib/permissions';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { to: '/', key: 'home', end: true },
  { to: '/player/top', key: 'top100', end: false },
  { to: '/season', key: 'seasons', end: false },
  { to: '/server', key: 'servers', end: false },
  { to: '/game', key: 'games', end: false },
] as const;

const LINK_BASE =
  'bevel bg-btn text-titletext font-display px-3 py-1.5 text-[11px] uppercase tracking-wide outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background';

export function TopNav() {
  const { t } = useTranslation('common');
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();

  const profileName = user ? (user.display_name ?? user.name) : '';
  const showAdminLink = canAccessAdminPanel(user);
  const playerName = user?.player?.name ?? null;
  // Legacy parity: no invite configured (DISCORD_JOIN_LINK) -> no button.
  const discordUrl = resolveDiscordUrl(import.meta.env.VITE_DISCORD_URL);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="bg-title border-b-2 border-b-bev-d">
      <nav className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-1.5 px-4 py-2 sm:px-5">
        <Link to="/" className="font-display text-strong mr-3 text-base no-underline">
          {t('brand')}
          <span className="text-orange">{t('brandSuffix')}</span>
        </Link>

        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.end}
            className={({ isActive }) => cn(LINK_BASE, isActive && 'text-orange')}
          >
            {t(`nav.${item.key}`)}
          </NavLink>
        ))}
        {status === 'authenticated' && (
          // Legacy header parity: the token page gets its own highlighted
          // entry so players find their `!login` code without the menu.
          <NavLink to="/token" className={cn(LINK_BASE, 'text-orange font-bold')}>
            {t('nav.loginToken')}
          </NavLink>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {discordUrl !== null && (
            <a
              href={discordUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="bevel-discord bg-discord font-display px-3 py-1.5 text-[11px] uppercase tracking-wide text-white outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('nav.discord')}
            </a>
          )}

          {status === 'authenticated' && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="bevel bg-btn flex items-center gap-2 px-2 py-1 outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={profileName}
              >
                <PlayerAvatar name={profileName} src={user.avatar} size={22} />
                <span className="font-display text-titletext hidden text-[11px] uppercase sm:inline">
                  {profileName}
                </span>
              </DropdownMenuTrigger>
              {/* Same order as the legacy header menu: account card, admin,
                  login token, profile, settings, then logout. */}
              <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuLabel className="flex items-center gap-2 font-normal">
                  <PlayerAvatar name={profileName} src={user.avatar} size={32} />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-strong truncate text-sm font-medium">{profileName}</span>
                    <span className="text-dim truncate text-xs">{user.email}</span>
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {showAdminLink && (
                  // The admin panel is a separate SPA on the same origin, so
                  // this is a full navigation, not a router <Link>.
                  <DropdownMenuItem asChild>
                    <a href={ADMIN_PANEL_URL}>
                      <ShieldCheck className="size-3.5" /> {t('nav.adminPanel')}
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/token">
                    <KeyRound className="size-3.5" /> {t('nav.loginToken')}
                  </Link>
                </DropdownMenuItem>
                {/* Profile = the linked in-game player (account name != player
                    name); hidden until the first verified !login links one. */}
                {playerName !== null && (
                  <DropdownMenuItem asChild>
                    <Link to={`/player/${encodeURIComponent(playerName)}`}>
                      <UserCircle className="size-3.5" /> {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="size-3.5" /> {t('nav.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout}>
                  <LogOut className="size-3.5" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <NavLink to="/login" className={LINK_BASE}>
                {t('nav.login')}
              </NavLink>
              <NavLink
                to="/register"
                className="bevel-orange bg-orange-cta font-display px-3 py-1.5 text-[11px] uppercase tracking-wide text-orange-cta-tx outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('nav.register')}
              </NavLink>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}
